import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { DropdownMenu } from "radix-ui"
import { FaPause } from "@react-icons/all-files/fa/FaPause"
import { FaPlay } from "@react-icons/all-files/fa/FaPlay"
import { TbArrowBarToLeft } from "@react-icons/all-files/tb/TbArrowBarToLeft"
import { TbArrowsDiagonal } from "@react-icons/all-files/tb/TbArrowsDiagonal"
import { TbArrowsDiagonalMinimize2 } from "@react-icons/all-files/tb/TbArrowsDiagonalMinimize2"
import { TbDots } from "@react-icons/all-files/tb/TbDots"
import { TbList } from "@react-icons/all-files/tb/TbList"
import { TbX } from "@react-icons/all-files/tb/TbX"
import { parseAsBoolean, parseAsInteger, useQueryState } from "nuqs"
import IconContainer from "app/player/components/IconContainer"
import { MODAL_QUERY_KEY } from "app/playlists/consts"
import NowPlayingInfo from "app/radio/components/NowPlayingInfo"
import { usePlay, useTogglePlaySpace } from "app/radio/components/PlayerContext"
import PlaylistControls from "app/radio/components/PlaylistControls"
import RadioBoxContextMenu from "app/radio/components/RadioBoxContextMenu"
import RadioOptions from "app/radio/components/RadioOptions"
import { TbMusic } from "@react-icons/all-files/tb/TbMusic"
import Tooltip from "app/radio/components/Tooltip"
import { TRANSITION } from "app/radio/consts"
import {
  currentPlayingItemAtom,
  isPlayingAtom,
  playingStateAtom,
  playlistInfoAtom,
} from "app/radio/state"
import { useAtom, useAtomValue } from "jotai/index"
import { useRouter } from "app/core/hooks/useRouter"
import { MutableRefObject, useMemo, useState } from "react"
import { DARK_BLUE_COLOR, DIMMED_BLUE_COLOR, ORANGE_COLOR } from "theme/colors"
import ButtonBase from "ui/Button"
import ContextMenuBase from "ui/ContextMenu"
import Group from "ui/Group"
import HrBase from "ui/Hr"
import { Content } from "./RadioBox"
import { Value } from "@stringtale/react"
import { FaTimes } from "@react-icons/all-files/fa/FaTimes"
import { DisplayStateType } from "../types"

const Button = styled(ButtonBase)<{ isActive?: boolean }>`
  ${TRANSITION};
  width: 48px;
  height: 48px;
  border-radius: 6px;
  background-color: ${DARK_BLUE_COLOR};
  color: ${(p) => (p.isActive ? ORANGE_COLOR : "white")};
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${(p) =>
    p.isActive &&
    css`
      &:hover {
        color: #c8320d;
      }
    `}
`

const RoundButton = styled(ButtonBase)`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${DARK_BLUE_COLOR};
  color: white;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CloseButton = styled.div`
  ${TRANSITION};
  height: 58px;
  cursor: pointer;
  box-sizing: border-box;
  border: 1px solid transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  &:hover {
    border: 1px solid ${DIMMED_BLUE_COLOR};
  }
`

const CloseButtonSmall = styled(RoundButton)`
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: absolute;
  border-radius: 100%;
  background-color: grey;
  top: -12px;
  right: -12px;
  border-radius: 100%;
  &:hover {
    border: inset 2px solid ${DIMMED_BLUE_COLOR};
  }
`

export const PlayButton = styled(RoundButton)`
  ${TRANSITION};
  width: 48px;
  height: 48px;
  background-color: #ed7044;

  svg {
    width: 20px;
    height: 20px;
    flex: 1 0 20px;
  }

  &:hover {
    background-color: #c8320d;
  }
`

const OptionsButton = styled(RoundButton)<{ dropdownActive?: boolean }>`
  ${TRANSITION};
  border: 1px solid #353371;
  &:hover {
    border: 1px solid ${DIMMED_BLUE_COLOR};
  }
  ${(p) =>
    p.dropdownActive
      ? css`
          color: ${DARK_BLUE_COLOR};
          background-color: #fff;
        `
      : css`
          color: #fff;
          background-color: ${DARK_BLUE_COLOR};
        `}
`

const ContextMenuBaseItem = styled(ContextMenuBase.Item)`
  padding: 8px 8px;
`

const Hr = styled(HrBase)`
  height: 1px;
`

const RadioBoxContent = ({
  containerRef,
  libraryOpen,
}: {
  containerRef: MutableRefObject<any>
  libraryOpen: boolean
}) => {
  const router = useRouter()
  const playlistInfo = useAtomValue(playlistInfoAtom)
  const isPlaying = useAtomValue(isPlayingAtom)

  const [playingState, setPlayingState] = useAtom(playingStateAtom)
  const currentPlayingItem = useAtomValue(currentPlayingItemAtom)

  const [dropdownActive, setDropdownActive] = useState<boolean>(false)

  const [_, setOpen] = useQueryState(MODAL_QUERY_KEY, parseAsInteger)

  useTogglePlaySpace()
  const { togglePlay, pause } = usePlay()

  const ContextMenu = useMemo(
    () => (
      <RadioBoxContextMenu
        open={dropdownActive}
        onOpenChange={setDropdownActive}
        trigger={
          <OptionsButton dropdownActive={dropdownActive}>
            <TbDots />
          </OptionsButton>
        }
        items={
          <>
            <DropdownMenu.Item
              asChild
              onClick={() =>
                playingState.type === "VISIBLE" &&
                setPlayingState({
                  ...playingState,
                  displayState: DisplayStateType["MINIMIZED"],
                })
              }
            >
              <ContextMenuBaseItem>
                <Group gap="10px">
                  <TbArrowBarToLeft size={20} />
                  <Value name="apps.web.src.radio.components.radioboxcontent.radio_verbergen">
                    Radio verbergen
                  </Value>
                </Group>
              </ContextMenuBaseItem>
            </DropdownMenu.Item>
            {playingState.type === "VISIBLE" &&
              playingState.displayState === DisplayStateType["SMALL"] && (
                <DropdownMenu.Item
                  asChild
                  onClick={() =>
                    setPlayingState({
                      ...playingState,
                      displayState: DisplayStateType["LARGE"],
                    })
                  }
                >
                  <ContextMenuBaseItem>
                    <Group gap="10px">
                      <TbArrowsDiagonal size={20} />
                      <Value name="apps.web.src.radio.components.radioboxcontent.radio_vergroten">
                        Radio vergroten
                      </Value>
                    </Group>
                  </ContextMenuBaseItem>
                </DropdownMenu.Item>
              )}
            {playingState.type === "VISIBLE" &&
              playingState.displayState === DisplayStateType["LARGE"] && (
                <DropdownMenu.Item
                  asChild
                  onClick={() =>
                    setPlayingState({
                      ...playingState,
                      displayState: DisplayStateType["SMALL"],
                    })
                  }
                >
                  <ContextMenuBaseItem>
                    <Group gap="10px">
                      <TbArrowsDiagonalMinimize2 size={20} />
                      <Value name="apps.web.src.radio.components.radioboxcontent.radio_verkleinen">
                        Radio verkleinen
                      </Value>
                    </Group>
                  </ContextMenuBaseItem>
                </DropdownMenu.Item>
              )}
            <Hr />
            <DropdownMenu.Item
              asChild
              onClick={async () => {
                if (!playlistInfo) return
                if (playlistInfo?.route)
                  return await router.push(playlistInfo.route.href)
                if (playlistInfo?.libraryPlaylistId)
                  return setOpen(playlistInfo.libraryPlaylistId)
              }}
            >
              <ContextMenuBaseItem>
                <Group gap="10px">
                  <TbList size={20} />
                  <Value name="apps.web.src.radio.components.radioboxcontent.ga_naar_lijst">
                    Ga naar lijst
                  </Value>
                </Group>
              </ContextMenuBaseItem>
            </DropdownMenu.Item>
            {currentPlayingItem && (
              <DropdownMenu.Item
                asChild
                onClick={async () => {
                  if (!currentPlayingItem.lesson) return
                  await router.push(`/les/${currentPlayingItem.lesson.slug}`)
                }}
              >
                <ContextMenuBaseItem>
                  <Group gap="10px">
                    <TbMusic size={20} />
                    <Value name="apps.web.src.radio.components.radioboxcontent.ga_naar_het_lied">
                      Ga naar het lied
                    </Value>
                  </Group>
                </ContextMenuBaseItem>
              </DropdownMenu.Item>
            )}
            <Hr />
            <DropdownMenu.Item
              asChild
              onClick={async () => {
                await pause()
                await setPlayingState({
                  type: "NONE",
                })
              }}
            >
              <ContextMenuBaseItem>
                <Group gap="10px">
                  <TbX size={20} />
                  <Value name="apps.web.src.radio.components.radioboxcontent.sluit_radio">
                    Sluit radio
                  </Value>
                </Group>
              </ContextMenuBaseItem>
            </DropdownMenu.Item>
          </>
        }
      />
    ),
    [
      dropdownActive,
      playingState,
      currentPlayingItem,
      setPlayingState,
      playlistInfo,
      router,
      setOpen,
      pause,
    ]
  )

  return (
    <Content>
      {!libraryOpen && playingState.type === "VISIBLE" && (
        <CloseButton
          onClick={() =>
            setPlayingState({
              ...playingState,
              displayState: DisplayStateType["MINIMIZED"],
            })
          }
        >
          <IconContainer
            url={"/icons/player/ico_chev_left_w.svg"}
            width="25px"
            height="25px"
          />
        </CloseButton>
      )}
      <NowPlayingInfo libraryOpen={libraryOpen} />
      {(playingState.type === "VISIBLE" &&
        playingState.displayState === DisplayStateType["LARGE"]) ||
      libraryOpen ? (
        <PlaylistControls containerRef={containerRef} />
      ) : (
        <Group gap="10px" align="center">
          <Tooltip
            text={isPlaying ? "Pauzeer" : "Afspelen"}
            trigger={
              <PlayButton onClick={togglePlay} disabled={!playingState}>
                {isPlaying ? <FaPause /> : <FaPlay style={{ marginLeft: 4 }} />}
              </PlayButton>
            }
          />
          {ContextMenu}
        </Group>
      )}
      {(playingState.type === "VISIBLE" &&
        playingState.displayState === DisplayStateType["LARGE"]) ||
      libraryOpen ? (
        <RadioOptions
          containerRef={containerRef}
          contextMenu={libraryOpen ? null : ContextMenu}
        />
      ) : null}
      {libraryOpen ? (
        <CloseButtonSmall
          onClick={async () => {
            // await setItem(null)
            await setPlayingState({ type: "NONE" })
            await pause()
          }}
        >
          <FaTimes size={20} />
        </CloseButtonSmall>
      ) : null}
    </Content>
  )
}

export default RadioBoxContent
